import type { Column, Relationship, Schema, Table } from '@hyphen-db-studio/schema-core';

function quoteIdent(name: string): string {
  return `"${name}"`;
}

function columnDdl(column: Column): string {
  const parts = [quoteIdent(column.name), column.type];
  if (column.settings.pk) parts.push('PRIMARY KEY');
  if (column.settings.increment) parts.push('GENERATED ALWAYS AS IDENTITY');
  if (column.settings.unique && !column.settings.pk) parts.push('UNIQUE');
  if (column.settings.notNull && !column.settings.pk) parts.push('NOT NULL');
  if (column.settings.default !== undefined && column.settings.default !== '') {
    parts.push(`DEFAULT ${column.settings.default}`);
  }
  return `  ${parts.join(' ')}`;
}

function tableDdl(table: Table): string {
  const lines = table.columns.map(columnDdl);
  const commentLines: string[] = [];
  if (table.note) commentLines.push(`COMMENT ON TABLE ${quoteIdent(table.name)} IS '${table.note.replace(/'/g, "''")}';`);
  for (const column of table.columns) {
    if (column.settings.note) {
      commentLines.push(
        `COMMENT ON COLUMN ${quoteIdent(table.name)}.${quoteIdent(column.name)} IS '${column.settings.note.replace(/'/g, "''")}';`,
      );
    }
  }
  const ddl = `CREATE TABLE ${quoteIdent(table.name)} (\n${lines.join(',\n')}\n);`;
  return commentLines.length ? `${ddl}\n${commentLines.join('\n')}` : ddl;
}

function foreignKeyDdl(rel: Relationship, schema: Schema): string | null {
  const fromTable = schema.tables.find((t) => t.id === rel.fromTableId);
  const fromColumn = fromTable?.columns.find((c) => c.id === rel.fromColumnId);
  const toTable = schema.tables.find((t) => t.id === rel.toTableId);
  const toColumn = toTable?.columns.find((c) => c.id === rel.toColumnId);
  if (!fromTable || !fromColumn || !toTable || !toColumn) return null;

  if (rel.type === '<>') {
    return `-- many-to-many: ${fromTable.name}.${fromColumn.name} <> ${toTable.name}.${toColumn.name} (create a join table to enforce this)`;
  }

  // '>' from is the many side (FK lives here); '<' is the reverse; '-' one-to-one (arbitrary owner: "from").
  const [ownerTable, ownerColumn, refTable, refColumn] =
    rel.type === '<' ? [toTable, toColumn, fromTable, fromColumn] : [fromTable, fromColumn, toTable, toColumn];

  const constraintName = `fk_${ownerTable.name}_${ownerColumn.name}`;
  return `ALTER TABLE ${quoteIdent(ownerTable.name)} ADD CONSTRAINT ${quoteIdent(constraintName)} FOREIGN KEY (${quoteIdent(ownerColumn.name)}) REFERENCES ${quoteIdent(refTable.name)} (${quoteIdent(refColumn.name)});`;
}

export function generateSql(schema: Schema): string {
  const sections: string[] = [];

  if (schema.enums.length) {
    sections.push(
      schema.enums
        .map((en) => `CREATE TYPE ${quoteIdent(en.name)} AS ENUM (${en.values.map((v) => `'${v.name}'`).join(', ')});`)
        .join('\n'),
    );
  }

  if (schema.tables.length) {
    sections.push(schema.tables.map(tableDdl).join('\n\n'));
  }

  if (schema.relationships.length) {
    const fks = schema.relationships.map((rel) => foreignKeyDdl(rel, schema)).filter((s): s is string => !!s);
    if (fks.length) sections.push(fks.join('\n'));
  }

  return sections.join('\n\n') + '\n';
}
